import { hrApiClient } from '@/lib/api';
import {
  CompanyContentDTO,
  CompanyDetailDTO,
  CompanyListDTO,
  FeaturedCompanyListDTO,
  GoldenCompanyListDTO,
  IFilter,
  IInsertCompanyFeedback,
  IItemResponse,
  IListResponse,
} from '@/shared';
import { CompanyAwardItem } from '@/features/company/types/company-awards.types';
import { CompanyListParams } from '@/features/company/types/company-list.types';

export const getFollowedCompanies = async (
  params: IFilter = {}
): Promise<IListResponse<CompanyListDTO>> => {
  const { data } = await hrApiClient.get('/company/public/followed', {
    params,
  });
  return data;
};

export type ToggleFollowCompanyResult = {
  followed: boolean;
};

export const toggleFollowCompany = async (
  companyId: number
): Promise<ToggleFollowCompanyResult> => {
  const { data } = await hrApiClient.post<
    ToggleFollowCompanyResult | IItemResponse<ToggleFollowCompanyResult>
  >(`/company/public/follow/${companyId}`);

  if (
    data &&
    typeof data === 'object' &&
    'data' in data &&
    data.data &&
    typeof data.data === 'object' &&
    'followed' in data.data
  ) {
    return { followed: Boolean(data.data.followed) };
  }

  if (data && typeof data === 'object' && 'followed' in data) {
    return { followed: Boolean(data.followed) };
  }

  throw new Error('Follow хариу буруу байна');
};

export function omitEmptyCompanyListParams(
  params: CompanyListParams
): CompanyListParams {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    })
  ) as CompanyListParams;
}

export const getCompanyList = async (params: CompanyListParams) => {
  const response = await hrApiClient.get<IListResponse<CompanyListDTO>>(
    `/company/public/list`,
    { params: omitEmptyCompanyListParams(params) }
  );
  return response.data;
};

export const getCompanyDetail = async (alias: string) => {
  const response = await hrApiClient.get<IItemResponse<CompanyDetailDTO>>(
    `/company/public/detail/${alias}`
  );
  return response.data;
};

export const getFeaturedCompanyList = async (): Promise<
  FeaturedCompanyListDTO[]
> => {
  const data = await hrApiClient.get('/company/public/featured-company');
  return data.data;
};

export const getGoldenCompanyList = async (): Promise<
  GoldenCompanyListDTO[]
> => {
  const data = await hrApiClient.get('/company/public/golden-company');
  return data.data;
};

export const getCompanyGroups = async (groupId: number) => {
  const response = await hrApiClient.get<IListResponse<CompanyListDTO>>(
    `/company/public/groups/${groupId}`
  );
  return response.data;
};

export const getCompanyContents = async (companyId: number) => {
  const response = await hrApiClient.get<IListResponse<CompanyContentDTO>>(
    `/company/public/contents/${companyId}`
  );
  return response.data;
};

export const getFollowedCompanyIds = async (): Promise<number[]> => {
  const { data } = await hrApiClient.get<
    IItemResponse<Array<number | string>>
  >('/company/public/followed-ids');
  const raw = Array.isArray(data) ? data : (data?.data ?? []);
  if (!Array.isArray(raw)) return [];
  return raw.map(Number).filter((id) => Number.isFinite(id));
};

export const insertCompanyFeedback = async (
  input: IInsertCompanyFeedback
): Promise<IItemResponse<{ id: number }>> => {
  const { data } = await hrApiClient.post<IItemResponse<{ id: number }>>(
    '/company/public/feedback',
    input
  );
  return data;
};

export const getCompanyAwards = async (
  companyId: number
): Promise<CompanyAwardItem[]> => {
  const response = await hrApiClient.get('/gwa/item', {
    params: { companyId },
  });
  return response.data;
};
